const LLM_MAX_EDGE = 1600;
const LLM_INITIAL_JPEG_QUALITY = 0.82;
const LLM_MIN_JPEG_QUALITY = 0.58;
const LLM_TARGET_IMAGE_BYTES = 2500 * 1024;
const LLM_ALLOWED_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export async function resizePhotoForLlm(blob: Blob) {
  if (!LLM_ALLOWED_MIME_TYPES.has(blob.type)) {
    throw new Error(`Unsupported image type ${blob.type || 'unknown'}.`);
  }

  if (typeof globalThis.document === 'undefined') {
    throw new Error('Browser image resizing is not available.');
  }

  const resized = await resizeImageBlob(blob);

  if (resized.size > LLM_TARGET_IMAGE_BYTES) {
    throw new Error('Resized image is still too large for extraction upload.');
  }

  return resized;
}

async function resizeImageBlob(blob: Blob) {
  const image = await loadImageForResize(blob);

  try {
    const canvas = globalThis.document.createElement('canvas');
    const width = readImageWidth(image);
    const height = readImageHeight(image);
    const initialScale = Math.min(1, LLM_MAX_EDGE / Math.max(width, height));
    let quality = LLM_INITIAL_JPEG_QUALITY;

    for (let attempt = 0; attempt < 8; attempt += 1) {
      canvas.width = Math.max(1, Math.round(width * initialScale));
      canvas.height = Math.max(1, Math.round(height * initialScale));
      canvas.getContext('2d')?.drawImage(image, 0, 0, canvas.width, canvas.height);

      const candidate = await canvasToBlob(canvas, quality);

      if (candidate.size <= LLM_TARGET_IMAGE_BYTES) {
        return candidate;
      }

      quality = Math.max(LLM_MIN_JPEG_QUALITY, quality - 0.06);
    }

    return await canvasToBlob(canvas, quality);
  } finally {
    closeImageForResize(image);
  }
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Unable to encode resized image.'));
        }
      },
      'image/jpeg',
      quality,
    );
  });
}

function loadImageForResize(blob: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof globalThis.createImageBitmap === 'function') {
    return globalThis.createImageBitmap(blob);
  }

  return loadHtmlImage(blob);
}

function loadHtmlImage(blob: Blob) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Unable to decode image for resizing.'));
    };
    image.src = objectUrl;
  });
}

function readImageWidth(image: ImageBitmap | HTMLImageElement) {
  return 'naturalWidth' in image ? image.naturalWidth : image.width;
}

function readImageHeight(image: ImageBitmap | HTMLImageElement) {
  return 'naturalHeight' in image ? image.naturalHeight : image.height;
}

function closeImageForResize(image: ImageBitmap | HTMLImageElement) {
  if ('close' in image) {
    image.close();
  }
}
